import { LitElement, html, css, svg } from 'lit'
import { store } from '../store.js'
import { navigate } from '../router.js'
import { takeReturnTo } from '../return-to.js'
import { api, setAccessToken } from '../api.js'
import { brandLogo } from './brand-logo.js'

// Login (§7.1): agent Google Workspace SSO plus a client email/password form.
// Restyled to the BrandExtract design system — a dark eggplant hero with the
// mascot resting on a white sign-in card.
class PageLogin extends LitElement {
  static properties = {
    _email: { state: true },
    _password: { state: true },
    _error: { state: true },
    _showResend: { state: true },
    _resendMsg: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    /* Full-bleed dark hero. Fixed so it paints over app-root's <main> box; the
       page has no nav when unauthenticated. */
    :host {
      position: fixed; inset: 0; overflow: auto; z-index: 0;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      background: var(--gradient-hero); padding: 32px 16px;
      font-family: var(--font-sans); color: var(--eggplant-500);
    }
    .wrap { width: min(460px, 94vw); }

    .masthead { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 6px; }
    .masthead .logo { color: var(--white); display: block; }
    .masthead .logo svg { height: 40px; width: auto; }
    .masthead .tag {
      font-family: var(--font-eyebrow); font-size: 16px; font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: var(--chartreuse-500); padding-left: 16px;
      border-left: 1px solid rgba(255,255,255,0.28); position: relative; top: 6px;
    }

    .mascot { display: flex; justify-content: center; }
    .mascot img { width: 100%; height: auto; display: block; margin-bottom: -35px;
                  position: relative; z-index: 0; left: -10px; }

    .card {
      background: var(--white); border-radius: 14px; box-shadow: var(--shadow-card);
      padding: 40px 40px 32px; position: relative; z-index: 1;
    }
    h1 { margin: 0 0 6px; font-size: 28px; font-weight: 800; letter-spacing: -0.02em; color: var(--eggplant-500); }
    .sub { margin: 0 0 26px; font-size: 15px; color: var(--neutral-600); }

    form { display: grid; gap: 18px; }
    label { display: flex; flex-direction: column; gap: 8px; font-size: 16px; color: var(--label-grey); }
    input {
      height: 48px; background: var(--white); border: none; border-radius: 0;
      box-shadow: inset 0 0 0 2px var(--border-input); padding: 12px 16px;
      font-family: var(--font-sans); font-size: 16px; line-height: 24px; color: var(--eggplant-500);
      outline: none; box-sizing: border-box;
    }
    input:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }

    button.cta {
      margin-top: 24px; width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
      padding: 12px 24px; border: none; border-radius: 0; cursor: pointer;
      background: var(--chartreuse-500); color: var(--eggplant-500);
      transition: background-color .18s ease;
    }
    button.cta:hover { background: var(--chartreuse-600); }
    button.cta:disabled { opacity: 0.4; cursor: not-allowed; }
    .arrow { font-size: 17px; line-height: 0; }

    .links { display: flex; justify-content: space-between; margin-top: 16px; font-size: 14px; }
    .links a { color: var(--sky-500); font-weight: 600; cursor: pointer; text-decoration: none; }
    .links a:hover { text-decoration: underline; }

    .divider { display: flex; align-items: center; gap: 14px; margin: 26px 0 22px; }
    .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: var(--neutral-100); }
    .divider span { font-family: var(--font-eyebrow); font-size: 10.5px; font-weight: 700;
                    letter-spacing: 0.14em; text-transform: uppercase; color: var(--neutral-500); }

    a.google {
      width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
      padding: 12px 24px; border-radius: 0; text-decoration: none; cursor: pointer;
      background: transparent; color: var(--eggplant-500); box-shadow: inset 0 0 0 1px var(--eggplant-500);
      box-sizing: border-box; transition: background-color .18s ease, color .18s ease;
    }
    a.google:hover { background: var(--eggplant-500); color: var(--neutral-50); }

    .dev { text-align: center; margin: 18px 0 0; font-size: 14px; }
    .dev a { color: var(--sky-500); font-weight: 600; text-decoration: none; }
    .dev a:hover { text-decoration: underline; }

    .foot { text-align: center; margin-top: 22px; font-size: 12.5px; color: rgba(255,255,255,0.55); }

    .error { background: var(--white); color: #B4232B; box-shadow: inset 0 0 0 1px #B4232B;
             padding: 10px 14px; margin: 0 0 18px; font-size: 14px; }
    .error a { color: #B4232B; cursor: pointer; text-decoration: underline; font-weight: 700; }
    .success { background: var(--chartreuse-bg); color: var(--chartreuse-700);
               box-shadow: inset 0 0 0 1px var(--chartreuse-300); padding: 12px 14px; margin: 0 0 18px; font-size: 14px; }
  `

  constructor() {
    super()
    this._email = ''
    this._password = ''
    this._error = this._oauthError()
    this._showResend = false
    this._resendMsg = ''
    this._busy = false
  }

  _oauthError() {
    const reason = new URLSearchParams(window.location.search).get('error')
    if (reason === 'domain') return 'Please sign in with your @brandextract.com Google account.'
    if (reason === 'signin') return 'Google sign-in failed. Please try again.'
    return ''
  }

  get _isLocal() {
    return ['localhost', '127.0.0.1'].includes(window.location.hostname)
  }

  async _submit(e) {
    e.preventDefault()
    this._error = ''
    this._resendMsg = ''
    this._showResend = false
    this._busy = true
    try {
      const { accessToken, user } = await api.post('/auth/login', {
        email: this._email.trim(),
        password: this._password
      })
      setAccessToken(accessToken)
      store.set({ user })
      // Back to whatever they were trying to reach when they were sent here
      // (a ticket link from a notification email, most often).
      navigate(takeReturnTo() || '/')
    } catch (err) {
      if (err.code === 'EMAIL_UNVERIFIED') {
        this._error = 'Please verify your email before signing in — check your inbox for the link.'
        this._showResend = true
      } else {
        this._error = err.message || 'Sign-in failed.'
      }
    } finally {
      this._busy = false
    }
  }

  async _resend() {
    const email = this._email.trim()
    if (!email) { this._error = 'Enter your email above, then resend.'; return }
    this._showResend = false
    try {
      await api.post('/auth/verify/resend', { email })
      this._error = ''
      this._resendMsg = `Verification email sent to ${email} — check your inbox.`
    } catch (err) {
      this._error = err.message || 'Could not resend the verification email.'
      this._showResend = true
    }
  }

  _googleMark() {
    return svg`<svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>`
  }

  render() {
    return html`
      <div class="wrap">
        <div class="masthead">
          <span class="logo">${brandLogo(40)}</span>
          <span class="tag">Support</span>
        </div>
        <div class="mascot">
          <img src="/images/login-mascot.svg" alt="BrandExtract fortune teller mascot" />
        </div>
        <div class="card">
          <h1>Sign in</h1>
          <p class="sub">Manage your support requests and tickets.</p>

          ${this._error ? html`<div class="error">${this._error}
            ${this._showResend ? html`<div style="margin-top:0.45rem;">
              <a @click=${this._resend}>Resend verification email</a>
            </div>` : ''}
          </div>` : ''}
          ${this._resendMsg ? html`<div class="success">${this._resendMsg}</div>` : ''}

          <form @submit=${this._submit}>
            <label>Email
              <input type="email" required autocomplete="email" placeholder="you@company.com"
                .value=${this._email} @input=${e => (this._email = e.target.value)} />
            </label>
            <label>Password
              <input type="password" required autocomplete="current-password" placeholder="••••••••"
                .value=${this._password} @input=${e => (this._password = e.target.value)} />
            </label>
            <button class="cta" type="submit" ?disabled=${this._busy}>
              ${this._busy ? 'Signing in…' : html`Sign in <span class="arrow">→</span>`}
            </button>
          </form>

          <div class="links">
            <a @click=${() => navigate('/reset')}>Forgot password?</a>
            <a @click=${() => navigate('/register')}>Create an account</a>
          </div>

          <div class="divider"><span>BrandExtract staff</span></div>
          <a class="google" href="/auth/google">${this._googleMark()} Sign in with Google</a>

          ${this._isLocal ? html`
            <p class="dev"><a href="/auth/dev-login">Continue as dev agent →</a></p>
          ` : ''}
        </div>
        <p class="foot">© 2026 BrandExtract · Support</p>
      </div>
    `
  }
}

customElements.define('page-login', PageLogin)
